/**
 * Modal de criar/editar template próprio da Biblioteca.
 *
 * Modo 'create': cria na pasta (folderId) do projeto selecionado.
 * Modo 'edit'  : edita o template existente.
 *
 * Campos: nome interno, tipo (card/texto), título, subtítulo, imagem, texto
 * (button template) e botões. Preview Messenger-like ao vivo.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Loader2, Image as ImageIcon } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
    createCustomTemplate, updateCustomTemplate,
} from '@/services/api';
import type { CustomTemplate } from '@/types/template-library';

type TemplateType = 'generic' | 'button';
type ButtonType = 'url' | 'postback';
type ButtonForm = { title: string; type: ButtonType; url: string; payload: string };

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: 'create' | 'edit';
    /** Em edit: o template a editar. */
    template?: CustomTemplate | null;
    /** Em create: onde criar. */
    projectId?: number | null;
    folderId?: number | null;
    /** Invalida queries após salvar. */
    onSaved?: (newId?: number) => void;
}

const MAX_BUTTONS = 3;

export default function CustomTemplateFormDialog({
    open, onOpenChange, mode, template, projectId, folderId, onSaved,
}: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [name, setName] = useState('');
    const [templateType, setTemplateType] = useState<TemplateType>('generic');
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [textContent, setTextContent] = useState('');
    const [buttons, setButtons] = useState<ButtonForm[]>([]);

    // Inicializa o form ao abrir
    useEffect(() => {
        if (!open) return;
        if (mode === 'edit' && template) {
            setName(template.name || '');
            setTemplateType((template.template_type as TemplateType) || 'generic');
            setTitle(template.title || '');
            setSubtitle(template.subtitle || '');
            setImageUrl(template.image_url || '');
            setTextContent(template.text_content || '');
            setButtons((template.buttons_json || []).map((b) => {
                const bany = b as unknown as { title?: string; url?: string; type?: string; payload?: string };
                const type: ButtonType = bany.type === 'postback' ? 'postback' : 'url';
                return {
                    title: bany.title || '',
                    type,
                    url: bany.url || '',
                    payload: bany.payload || '',
                };
            }));
        } else {
            setName('');
            setTemplateType('generic');
            setTitle('');
            setSubtitle('');
            setImageUrl('');
            setTextContent('');
            setButtons([]);
        }
    }, [open, mode, template]);

    const mutation = useMutation({
        mutationFn: async () => {
            const buttonsPayload = buttons
                .filter((b) => b.title.trim() && (b.type === 'url' ? b.url.trim() : true))
                .map((b) => b.type === 'postback'
                    // Payload deriva do título (o webhook geralmente só quer saber "qual botão foi clicado")
                    ? { title: b.title, type: 'postback' as const, payload: b.title.trim() }
                    : { title: b.title, type: 'url' as const, url: b.url });
            if (mode === 'create') {
                return createCustomTemplate({
                    name,
                    project_id: projectId ?? null,
                    folder_id: folderId ?? null,
                    template_type: templateType,
                    title: title || undefined,
                    subtitle: templateType === 'generic' ? (subtitle || undefined) : undefined,
                    image_url: templateType === 'generic' ? (imageUrl || undefined) : undefined,
                    text_content: templateType === 'button' ? (textContent || undefined) : undefined,
                    buttons: buttonsPayload,
                });
            }
            return updateCustomTemplate(template!.id, {
                name,
                template_type: templateType,
                title: title || undefined,
                subtitle: templateType === 'generic' ? (subtitle || undefined) : undefined,
                image_url: templateType === 'generic' ? (imageUrl || undefined) : undefined,
                text_content: templateType === 'button' ? (textContent || undefined) : undefined,
                buttons: buttonsPayload,
            });
        },
        onSuccess: (data) => {
            toast({ title: mode === 'create' ? 'Template criado' : 'Template atualizado' });
            queryClient.invalidateQueries({ queryKey: ['custom-templates'] });
            queryClient.invalidateQueries({ queryKey: ['folder-tree'] });
            // Passa o id pro caller (usado pelo UtilityTemplatePicker pra
            // encadear submit-free em 1 page sandbox).
            const newId = (data as { id?: number } | undefined)?.id;
            onSaved?.(newId);
            onOpenChange(false);
        },
        onError: (e: any) => {
            toast({
                title: 'Falha ao salvar',
                description: e?.response?.data?.detail || e?.message || 'erro desconhecido',
                variant: 'destructive',
            });
        },
    });

    const addButton = () => {
        if (buttons.length >= MAX_BUTTONS) return;
        setButtons((p) => [...p, { title: '', type: 'url', url: '', payload: '' }]);
    };
    const updateButton = (i: number, field: keyof ButtonForm, v: string) => {
        setButtons((p) => p.map((b, idx) => (idx === i ? { ...b, [field]: v } : b)));
    };
    const removeButton = (i: number) => {
        setButtons((p) => p.filter((_, idx) => idx !== i));
    };

    const isGeneric = templateType === 'generic';
    const buttonsValid = buttons.every((b) => b.title.trim().length > 0
        && (b.type === 'postback' ? true : b.url.trim().length > 0));
    const isValid = name.trim().length > 0
        && (isGeneric ? title.trim().length > 0 : textContent.trim().length > 0)
        && buttonsValid;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {mode === 'create' ? 'Novo template' : 'Editar template'}
                    </DialogTitle>
                    <DialogDescription>
                        Modelo reutilizável em Broadcasts, Flows e Esteira.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Form */}
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="t-name">Nome do modelo (organização)</Label>
                            <Input
                                id="t-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                maxLength={255}
                                placeholder="Ex: Celta promo julho"
                            />
                        </div>

                        {/* Tipo */}
                        <div className="space-y-1.5">
                            <Label>Tipo</Label>
                            <div className="inline-flex border rounded-md h-9 overflow-hidden">
                                {([['generic', 'Card (imagem)'], ['button', 'Texto + botões']] as const).map(
                                    ([val, label], i) => (
                                        <button
                                            key={val}
                                            type="button"
                                            onClick={() => setTemplateType(val)}
                                            className={`px-3 text-xs transition-colors ${
                                                templateType === val
                                                    ? 'bg-violet-600 text-white'
                                                    : 'bg-white text-gray-600 hover:bg-gray-50'
                                            } ${i > 0 ? 'border-l' : ''}`}
                                        >
                                            {label}
                                        </button>
                                    ),
                                )}
                            </div>
                        </div>

                        {isGeneric ? (
                            <>
                                <div className="space-y-1.5">
                                    <Label htmlFor="t-title">Título</Label>
                                    <Input
                                        id="t-title" value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        maxLength={80} placeholder="Ex: CHEVROLET CELTA"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="t-sub">Descrição</Label>
                                    <Textarea
                                        id="t-sub" value={subtitle} rows={2}
                                        onChange={(e) => setSubtitle(e.target.value)}
                                        maxLength={80} placeholder="Texto secundário do card"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="t-img">URL da imagem</Label>
                                    <Input
                                        id="t-img" value={imageUrl}
                                        onChange={(e) => setImageUrl(e.target.value)}
                                        placeholder="https://..."
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="space-y-1.5">
                                <Label htmlFor="t-text">Texto</Label>
                                <Textarea
                                    id="t-text" value={textContent} rows={4}
                                    onChange={(e) => setTextContent(e.target.value)}
                                    placeholder="Texto da mensagem"
                                />
                            </div>
                        )}

                        {/* Botões */}
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <Label>Botões ({buttons.length}/{MAX_BUTTONS})</Label>
                                <Button
                                    type="button" size="sm" variant="outline"
                                    onClick={addButton} disabled={buttons.length >= MAX_BUTTONS}
                                >
                                    <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
                                </Button>
                            </div>
                            {buttons.length === 0 && (
                                <p className="text-[11px] text-gray-400 italic">
                                    Sem botões — clique em "Adicionar".
                                </p>
                            )}
                            <div className="space-y-2">
                                {buttons.map((b, i) => (
                                    <div key={i} className="border rounded-md p-2 space-y-2 bg-gray-50">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] uppercase text-gray-500 font-medium tracking-wide">
                                                Botão {i + 1}
                                            </span>
                                            <button
                                                type="button" onClick={() => removeButton(i)}
                                                className="text-gray-400 hover:text-red-600" title="Remover"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-[11px]">
                                            {(['url', 'postback'] as const).map((t) => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    onClick={() => updateButton(i, 'type', t)}
                                                    className={`px-2 py-0.5 rounded border transition ${
                                                        b.type === t
                                                            ? 'bg-violet-100 border-violet-300 text-violet-800 font-medium'
                                                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {t === 'url' ? '🔗 Link (URL)' : '💬 Postback'}
                                                </button>
                                            ))}
                                            {b.type === 'postback' && (
                                                <span className="text-gray-400 ml-auto">clique manda webhook</span>
                                            )}
                                        </div>
                                        <Input
                                            placeholder="Texto do botão (ex: Confirmar)"
                                            value={b.title} maxLength={20}
                                            onChange={(e) => updateButton(i, 'title', e.target.value)}
                                        />
                                        {b.type === 'url' && (
                                            <Input
                                                placeholder="https://destino.com/..."
                                                value={b.url}
                                                onChange={(e) => updateButton(i, 'url', e.target.value)}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Preview */}
                    <div className="space-y-2">
                        <Label>Preview</Label>
                        <div className="bg-gray-50 rounded-2xl rounded-tl-sm border border-gray-200 overflow-hidden max-w-[300px]">
                            {isGeneric && (
                                imageUrl ? (
                                    <div className="aspect-[1.91/1] bg-gray-100 overflow-hidden">
                                        <img
                                            src={imageUrl} alt="" loading="lazy"
                                            className="w-full h-full object-contain"
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                    </div>
                                ) : (
                                    <div className="aspect-[1.91/1] bg-gradient-to-br from-violet-50 to-sky-50 flex items-center justify-center">
                                        <ImageIcon className="w-8 h-8 text-gray-300" />
                                    </div>
                                )
                            )}
                            <div className="p-2.5 space-y-0.5">
                                {isGeneric ? (
                                    <>
                                        {title && (
                                            <p className="text-[13px] font-semibold text-gray-900 line-clamp-2 leading-tight">
                                                {title}
                                            </p>
                                        )}
                                        {subtitle && (
                                            <p className="text-[11px] text-gray-600 line-clamp-2 leading-tight">
                                                {subtitle}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-[12px] text-gray-900 leading-snug whitespace-pre-line line-clamp-5">
                                        {textContent || '(sem texto)'}
                                    </p>
                                )}
                            </div>
                            {buttons.length > 0 && (
                                <div className="border-t bg-white">
                                    {buttons.map((b, i) => (
                                        <div
                                            key={i}
                                            className={`px-3 py-1.5 text-center text-[11px] text-violet-600 font-medium truncate ${i > 0 ? 'border-t border-gray-100' : ''}`}
                                        >
                                            {b.title || '(sem texto)'}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button" variant="outline"
                        onClick={() => onOpenChange(false)} disabled={mutation.isPending}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="button" onClick={() => mutation.mutate()}
                        disabled={!isValid || mutation.isPending}
                    >
                        {mutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                        {mode === 'create' ? 'Criar' : 'Salvar'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
